import { useEffect, useRef, useState } from 'react';

type NetworkStatus = 'idle' | 'offline' | 'restored';

const getInitialStatus = (): NetworkStatus => {
  if (typeof navigator === 'undefined' || typeof navigator.onLine === 'undefined') {
    return 'idle';
  }

  return navigator.onLine ? 'idle' : 'offline';
};

const useNetworkStatusMonitor = (): NetworkStatus => {
  const [status, setStatus] = useState<NetworkStatus>(getInitialStatus());
  const timeoutRef = useRef<number | null>(null);

  const clearTimeoutIfAny = () => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    const handleOffline = () => {
      clearTimeoutIfAny();
      setStatus('offline');
    };

    const handleOnline = () => {
      clearTimeoutIfAny();
      setStatus('idle');

      timeoutRef.current = window.setTimeout(() => {
        setStatus('restored');

        timeoutRef.current = window.setTimeout(() => {
          setStatus('idle');
        }, 3000);
      }, 200);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      clearTimeoutIfAny();
    };
  }, []);

  return status;
};

export default useNetworkStatusMonitor;
